import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { ChevronLeft, Save, FileText, Upload, X, Eye, FileSignature, Contact, Loader2, FilePlus2, Sparkles, Copy, UserCog } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth, isAdmin, isPartner } from '@/contexts/AuthContext';
import { api, formatCurrency, readFileAsDataUrl } from '@/lib/api';
import { toast } from 'sonner';

const displayName = (c) => c && (c.customer_type === 'azienda' && c.company_name
  ? c.company_name
  : `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—');

const previewFile = (dataUrl) => {
  if (!dataUrl) return;
  try {
    const [meta, base64] = dataUrl.split(',');
    const mime = /data:(.*?);base64/.exec(meta || '')?.[1] || 'application/octet-stream';
    const bin = atob(base64 || '');
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error('Impossibile aprire l’anteprima'); }
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [uploadCategory, setUploadCategory] = useState('doc_identita');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  // Riassegnazione anagrafica
  const { user: me } = useAuth();
  const canReassign = isAdmin(me) || isPartner(me);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignTargetUserId, setAssignTargetUserId] = useState('');

  const load = async () => {
    try {
      const [c, ct, cats, qs] = await Promise.all([
        api.getCustomer(id),
        api.getCustomerContracts(id),
        api.getCustomerDocCategories(),
        api.listQuotes({ customer_id: id }).catch(() => ({ items: [] })),
      ]);
      setCustomer(c); setForm(c); setContracts(ct.items || []); setCategories(cats.categories || []);
      setQuotes(qs.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cliente non trovato');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (!customer) return <div className="p-6 text-muted-foreground">Caricamento…</div>;

  const catMap = Object.fromEntries(categories.map(c => [c.key, c.label]));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.updateCustomer(id, {
        customer_type: form.customer_type, first_name: form.first_name, last_name: form.last_name,
        company_name: form.company_name, fiscal_code: form.fiscal_code, vat_number: form.vat_number,
        email: form.email, phone: form.phone, birth_place: form.birth_place, birth_date: form.birth_date,
        address: form.address, city: form.city, province: form.province, cap: form.cap,
        iban: form.iban, notes: form.notes, tags: form.tags,
      });
      setCustomer(updated); setForm(updated);
      toast.success('Anagrafica aggiornata');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if ((form.tags || []).includes(t)) { setTagInput(''); return; }
    setForm(f => ({ ...f, tags: [...(f.tags || []), t] })); setTagInput('');
  };

  const removeTag = (t) => setForm(f => ({ ...f, tags: (f.tags || []).filter(x => x !== t) }));

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let uploaded = null;
    try {
      for (const file of files) {
        if (file.size > 6 * 1024 * 1024) {
          toast.error(`"${file.name}" supera i 6MB e verrà ignorato`);
          continue;
        }
        const dataUrl = await readFileAsDataUrl(file);
        uploaded = await api.uploadCustomerDoc(id, {
          category: uploadCategory, filename: file.name, data_url: dataUrl, notes: uploadNotes,
        });
      }
      if (uploaded) { setCustomer(uploaded); setForm(uploaded); }
      toast.success('Documento caricato');
      setUploadNotes('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento');
    } finally { setUploading(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Rimuovere questo documento?')) return;
    try {
      const updated = await api.deleteCustomerDoc(id, docId);
      setCustomer(updated); setForm(updated);
      toast.success('Documento rimosso');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="customer-detail">
      <div className="flex flex-col md:flex-row md:items-start gap-3 md:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild><Link to="/customers"><ChevronLeft className="size-4 mr-1" />Torna alle anagrafiche</Link></Button>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2"><Contact className="size-5 text-primary" />{displayName(customer)}</h1>
            <div className="text-xs text-muted-foreground">
              {customer.fiscal_code && <span className="font-mono">CF: {customer.fiscal_code}</span>}
              {customer.vat_number && <span className="ml-2 font-mono">P.IVA: {customer.vat_number}</span>}
              {customer.created_by_name && <span className="ml-2">· creata da {customer.created_by_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canReassign && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                setAssignTargetUserId('');
                setAssignOpen(true);
                try {
                  const users = await api.listUsers();
                  const filtered = isAdmin(me) ? users : users.filter(u => u.company_id === me?.company_id);
                  setAssignableUsers(filtered);
                } catch { setAssignableUsers([]); }
              }}
              data-testid="customer-assign-btn"
              title="Riassegna anagrafica a un altro utente/agente"
            >
              <UserCog className="size-4 mr-1" />
              {customer.assigned_to_name ? `Assegnato: ${customer.assigned_to_name}` : 'Assegna a…'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/contracts/new?customerId=${id}&mode=quote`)}
            data-testid="customer-new-quote-btn"
          >
            <Sparkles className="size-4 mr-1" />Nuovo Preventivo
          </Button>
          <Button
            size="sm"
            onClick={() => navigate(`/contracts/new?customerId=${id}`)}
            data-testid="customer-new-contract-btn"
          >
            <FilePlus2 className="size-4 mr-1" />Nuovo Contratto
          </Button>
        </div>
      </div>

      <Tabs defaultValue="anagrafica" className="space-y-4">
        <TabsList>
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica">Anagrafica</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti <Badge variant="secondary" className="ml-1">{(customer.documents || []).length}</Badge></TabsTrigger>
          <TabsTrigger value="preventivi" data-testid="tab-preventivi">Preventivi <Badge variant="secondary" className="ml-1">{quotes.length}</Badge></TabsTrigger>
          <TabsTrigger value="contratti" data-testid="tab-contratti">Contratti <Badge variant="secondary" className="ml-1">{contracts.length}</Badge></TabsTrigger>
        </TabsList>

        <TabsContent value="anagrafica">
          <Card>
            <CardHeader><CardTitle className="text-base">Dati principali</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.customer_type || 'privato'} onValueChange={(v) => setForm(f => ({ ...f, customer_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="privato">Privato</SelectItem><SelectItem value="azienda">Azienda</SelectItem></SelectContent>
                </Select>
              </div>
              {form.customer_type === 'azienda' ? (
                <>
                  <div><Label>Ragione sociale</Label><Input value={form.company_name || ''} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} /></div>
                  <div><Label>P.IVA</Label><Input value={form.vat_number || ''} onChange={(e) => setForm(f => ({ ...f, vat_number: e.target.value.toUpperCase() }))} /></div>
                </>
              ) : (
                <>
                  <div><Label>Nome</Label><Input value={form.first_name || ''} onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} /></div>
                  <div><Label>Cognome</Label><Input value={form.last_name || ''} onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} /></div>
                </>
              )}
              <div><Label>Codice fiscale</Label><Input value={form.fiscal_code || ''} onChange={(e) => setForm(f => ({ ...f, fiscal_code: e.target.value.toUpperCase() }))} /></div>
              <div><Label>IBAN</Label><Input value={form.iban || ''} onChange={(e) => setForm(f => ({ ...f, iban: e.target.value.toUpperCase() }))} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email || ''} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div><Label>Telefono</Label><Input value={form.phone || ''} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
              <div className="md:col-span-2"><Label>Indirizzo</Label><Input value={form.address || ''} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} /></div>
              <div><Label>Città</Label><Input value={form.city || ''} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Prov.</Label><Input value={form.province || ''} maxLength={2} onChange={(e) => setForm(f => ({ ...f, province: e.target.value.toUpperCase() }))} /></div>
                <div><Label>CAP</Label><Input value={form.cap || ''} onChange={(e) => setForm(f => ({ ...f, cap: e.target.value }))} /></div>
              </div>
              <div className="md:col-span-2">
                <Label>Note interne</Label>
                <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <Label>Tag (cross-sell / up-sell / stato)</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {(form.tags || []).map(t => (
                    <Badge key={t} variant="secondary" className="gap-1">
                      {t}
                      <button type="button" onClick={() => removeTag(t)} className="ml-1 hover:text-destructive"><X className="size-3" /></button>
                    </Badge>
                  ))}
                  <div className="flex items-center gap-1">
                    <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Nuovo tag…" className="h-8 w-40" />
                    <Button type="button" size="sm" variant="outline" onClick={addTag}>+</Button>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button onClick={handleSave} disabled={saving} data-testid="customer-save-btn">
                  {saving ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Save className="size-4 mr-1" />}
                  Salva modifiche
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documenti">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Documenti del cliente</CardTitle>
              <CardDescription>Carica qui i documenti necessari per l’evasione delle pratiche (doc. identità, IBAN, visure, foto, bollette POD/PDR, buste paga, CUD/730 in caso di finanziamento).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-secondary/20 p-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                <div>
                  <Label>Categoria</Label>
                  <Select value={uploadCategory} onValueChange={setUploadCategory}>
                    <SelectTrigger data-testid="customer-upload-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Note (opzionale)</Label>
                  <Input value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} placeholder="es. Carta identità scad. 2028" />
                </div>
                <div className="md:col-span-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border-2 border-dashed px-4 py-3 hover:bg-secondary/40 text-sm w-full justify-center">
                    {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                    <span>{uploading ? 'Caricamento…' : 'Trascina o seleziona file (PDF/immagine · max 6MB per file)'}</span>
                    <input
                      type="file"
                      multiple
                      accept="application/pdf,image/*"
                      className="sr-only"
                      onChange={async (e) => { await handleUpload(Array.from(e.target.files || [])); e.target.value = ''; }}
                      data-testid="customer-upload-input"
                    />
                  </label>
                </div>
              </div>

              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Caricato da</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(customer.documents || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nessun documento caricato</TableCell></TableRow>}
                    {(customer.documents || []).map(d => (
                      <TableRow key={d.id} data-testid={`customer-doc-${d.id}`}>
                        <TableCell><Badge variant="outline">{catMap[d.category] || d.category}</Badge></TableCell>
                        <TableCell className="max-w-[240px] truncate text-sm" title={d.filename}><FileText className="size-3.5 inline mr-1 text-muted-foreground" />{d.filename}</TableCell>
                        <TableCell className="text-xs">
                          <div>{d.uploaded_by_name || '—'}</div>
                          <div className="text-muted-foreground">{d.uploaded_at?.slice(0, 16).replace('T', ' ')}</div>
                        </TableCell>
                        <TableCell className="text-xs max-w-[240px] truncate" title={d.notes}>{d.notes || '—'}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => previewFile(d.data_url)} title="Anteprima"><Eye className="size-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteDoc(d.id)} title="Elimina"><X className="size-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preventivi">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Preventivi generati</CardTitle>
              <CardDescription>Elenco dei preventivi creati per questo cliente. Usa &quot;Duplica&quot; per generarne uno nuovo con prodotti/prezzi modificati, senza reinserire l&apos;anagrafica.</CardDescription>
            </CardHeader>
            <CardContent>
              {quotes.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">Nessun preventivo per questo cliente.</div>
              ) : (
                <div className="rounded-lg border overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Titolo</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Creato da</TableHead>
                      <TableHead className="text-right">Totale</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {quotes.map(q => (
                        <TableRow key={q.id} data-testid={`customer-quote-row-${q.id}`}>
                          <TableCell className="font-medium max-w-[280px] truncate" title={q.title}>{q.title || 'Preventivo'}</TableCell>
                          <TableCell className="text-xs">{q.created_at?.slice(0, 16).replace('T', ' ')}</TableCell>
                          <TableCell><Badge variant={q.status === 'convertito' ? 'default' : 'outline'}>{q.status || 'attivo'}</Badge></TableCell>
                          <TableCell className="text-xs">{q.created_by_name || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(q.totals?.total || 0)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/contracts/new?duplicateQuoteId=${q.id}`)}
                              data-testid={`customer-duplicate-quote-${q.id}`}
                            >
                              <Copy className="size-3.5 mr-1" />Duplica
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contratti">
          {contracts.length > 0 && (() => {
            const byStatus = contracts.reduce((acc, c) => {
              const st = c.status || 'bozza';
              acc[st] = acc[st] || { count: 0, amount: 0 };
              acc[st].count += 1;
              acc[st].amount += Number(c.totals?.total || 0);
              return acc;
            }, {});
            const totalCount = contracts.length;
            const totalAmount = contracts.reduce((s, c) => s + Number(c.totals?.total || 0), 0);
            const signedCount = (byStatus.firmato?.count || 0);
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="customer-contracts-summary">
                <Card><CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground">Totale contratti</div>
                  <div className="text-xl font-semibold tabular-nums">{totalCount}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground">Importo totale</div>
                  <div className="text-xl font-semibold tabular-nums">{formatCurrency(totalAmount)}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground">Firmati</div>
                  <div className="text-xl font-semibold tabular-nums text-primary">{signedCount}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                  <div className="text-xs text-muted-foreground">In attesa</div>
                  <div className="text-xl font-semibold tabular-nums">{(byStatus.inviato?.count || 0) + (byStatus.compilato?.count || 0) + (byStatus.bozza?.count || 0)}</div>
                </CardContent></Card>
              </div>
            );
          })()}
          <Card>
            <CardHeader><CardTitle className="text-base">Contratti collegati</CardTitle></CardHeader>
            <CardContent>
              {contracts.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">Nessun contratto collegato a questo cliente.</div>
              ) : (
                <div className="rounded-lg border overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Numero</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Firma</TableHead>
                      <TableHead className="text-right">Totale</TableHead>
                      <TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {contracts.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.contract?.number || c.id.slice(0, 8)}</TableCell>
                          <TableCell>{c.contract?.date}</TableCell>
                          <TableCell><Badge variant="outline">{c.status}</Badge></TableCell>
                          <TableCell>
                            {c.signature?.signature_mode === 'otp'
                              ? <Badge className="gap-1 bg-primary/10 text-primary border-primary/30"><FileSignature className="size-3" />OTP</Badge>
                              : c.signature?.customer_signature_data_url ? <Badge variant="secondary">Manuale</Badge> : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(c.totals?.total || 0)}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" asChild><Link to={`/contracts/${c.id}`}>Apri</Link></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Riassegna anagrafica */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="customer-assign-dialog">
          <DialogHeader>
            <DialogTitle>Riassegna anagrafica</DialogTitle>
            <DialogDescription>
              Trasferisci il contatto a un altro utente/agente. {isAdmin(me) ? 'Come Admin puoi assegnare a qualsiasi utente di qualsiasi azienda: l\'anagrafica verrà spostata nella company dell\'utente destinatario.' : 'Puoi assegnare solo a utenti della tua azienda.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">Utente destinatario</Label>
            <Select value={assignTargetUserId} onValueChange={setAssignTargetUserId}>
              <SelectTrigger data-testid="customer-assign-user"><SelectValue placeholder="Seleziona utente…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {assignableUsers.length === 0 && <SelectItem value="__none" disabled>Nessun utente disponibile</SelectItem>}
                {assignableUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name || u.email} · <span className="text-muted-foreground">{u.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Annulla</Button>
            <Button
              onClick={async () => {
                if (!assignTargetUserId) { toast.error('Seleziona un utente'); return; }
                try {
                  await api.assignCustomer(id, assignTargetUserId);
                  toast.success('Anagrafica riassegnata');
                  setAssignOpen(false);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Impossibile riassegnare');
                }
              }}
              disabled={!assignTargetUserId}
              data-testid="customer-assign-save"
            >
              <UserCog className="size-4 mr-2" /> Riassegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
